// biome-ignore lint/suspicious/noExplicitAny: perf metadata can be anything
type PerfMeta = Record<string, any>;

type MetricKey = string;

interface MetricStore {
    values: number[];
    maxSize: number;
    last?: number;
    count: number;
    sum: number;
    max: number;
}

interface LongTaskAgg {
    count: number;
    total: number;
    max: number;
}

const MAX_VALUES = 200;

function clamp01(n: number): number {
    if (Number.isNaN(n)) return 0;
    if (n < 0) return 0;
    if (n > 1) return 1;
    return n;
}

// biome-ignore lint/suspicious/noExplicitAny: accepts unknown runtime value
function safeNumber(n: any, fallback: number): number {
    if (typeof n !== 'number') return fallback;
    if (Number.isNaN(n)) return fallback;
    return n;
}

function readSampleRate(): number {
    if (typeof window === 'undefined') return 0;
    try {
        const v = window.localStorage?.getItem('pdf_perf_sample_rate');
        if (!v) return 0.01;
        return clamp01(Number(v));
    } catch {
        return 0.01;
    }
}

export function isPerfPanelEnabled(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        // biome-ignore lint/suspicious/noExplicitAny: custom window property
        const w: any = window as any;
        if (w.__PDF_PERF_PANEL__ === true) return true;
        const params = new URLSearchParams(window.location.search);
        if (params.get('pdfPerf') === '1') return true;
        if (window.localStorage?.getItem('pdf_perf_panel') === '1') return true;
    } catch {
        return false;
    }
    return false;
}

function isSampled(sampleRate: number): boolean {
    if (typeof window === 'undefined') return false;
    try {
        // 面板开启时强制采样，保证前端可查看
        if (isPerfPanelEnabled()) return true;

        const key = 'pdf_perf_sampled';
        const existed = window.sessionStorage?.getItem(key);
        if (existed === '1') return true;
        if (existed === '0') return false;

        const hit = Math.random() < sampleRate;
        window.sessionStorage?.setItem(key, hit ? '1' : '0');
        return hit;
    } catch {
        return false;
    }
}

const sampleRate = readSampleRate();
const sampled = isSampled(sampleRate);

const startTimes = new Map<MetricKey, number>();
const metrics = new Map<MetricKey, MetricStore>();
const longTaskAgg: LongTaskAgg = { count: 0, total: 0, max: 0 };

let longTaskObserverInited = false;

function ensureLongTaskObserver(): void {
    if (!sampled) return;
    if (longTaskObserverInited) return;
    longTaskObserverInited = true;
    if (typeof window === 'undefined') return;
    if (typeof PerformanceObserver === 'undefined') return;
    try {
        const obs = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                const d = safeNumber(entry.duration, 0);
                if (d <= 0) continue;
                longTaskAgg.count += 1;
                longTaskAgg.total += d;
                if (d > longTaskAgg.max) longTaskAgg.max = d;
            }
        });
        obs.observe({ entryTypes: ['longtask'] });
    } catch {
        // ignore
    }
}

function pushMetric(key: MetricKey, value: number): void {
    if (!sampled) return;
    const v = safeNumber(value, -1);
    if (v < 0) return;
    let store = metrics.get(key);
    if (!store) {
        store = { values: [], maxSize: MAX_VALUES, count: 0, sum: 0, max: 0 };
        metrics.set(key, store);
    }
    store.last = v;
    store.count += 1;
    store.sum += v;
    if (v > store.max) store.max = v;
    store.values.push(v);
    if (store.values.length > store.maxSize) {
        store.values.shift();
    }
}

export function perfStart(name: string, key: string, meta?: PerfMeta): void {
    if (!sampled) return;
    ensureLongTaskObserver();
    // meta 当前仅用于对齐调用方，避免未来扩展时改动
    void meta;
    startTimes.set(`${name}:${key}`, performance.now());
}

export function perfEnd(name: string, key: string, meta?: PerfMeta): void {
    if (!sampled) return;
    ensureLongTaskObserver();
    void meta;
    const k = `${name}:${key}`;
    const start = startTimes.get(k);
    if (start === undefined) return;
    startTimes.delete(k);
    pushMetric(k, performance.now() - start);
}

export function perfMeasure(name: string, value: number, key: string, meta?: PerfMeta): void {
    if (!sampled) return;
    ensureLongTaskObserver();
    void meta;
    pushMetric(`${name}:${key}`, value);
}

export function isPerfSampled(): boolean {
    return sampled;
}

export interface PdfPerfConfig {
    imageCacheSize?: number;
    imageParallel?: number;
    imagePreCount?: number;
    imageNextCount?: number;
}

// biome-ignore lint/suspicious/noExplicitAny: accepts unknown runtime value
function safeInt(v: any, fallback: number): number {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return Math.floor(n);
}

/**
 * 仅在 Perf Panel 开启时读取（避免默认产生额外开销）
 *
 * 可通过 localStorage 配置：
 * - pdf_perf_image_cache_size
 * - pdf_perf_image_parallel
 * - pdf_perf_image_pre_count
 * - pdf_perf_image_next_count
 */
export function getPdfPerfConfig(): PdfPerfConfig {
    if (!isPerfPanelEnabled()) {
        return {};
    }
    if (typeof window === 'undefined') {
        return {};
    }
    try {
        const cacheSize = safeInt(window.localStorage?.getItem('pdf_perf_image_cache_size'), 0);
        const parallel = safeInt(window.localStorage?.getItem('pdf_perf_image_parallel'), 0);
        const preCount = safeInt(window.localStorage?.getItem('pdf_perf_image_pre_count'), 0);
        const nextCount = safeInt(window.localStorage?.getItem('pdf_perf_image_next_count'), 0);
        return {
            imageCacheSize: cacheSize > 0 ? cacheSize : undefined,
            imageParallel: parallel > 0 ? parallel : undefined,
            imagePreCount: preCount > 0 ? preCount : undefined,
            imageNextCount: nextCount > 0 ? nextCount : undefined,
        };
    } catch {
        return {};
    }
}
